/**
 * TASK KANBAN CARD — adattatore entity-specific della card task per la bacheca.
 *
 * Non ridisegna lo scheletro (root/top/titolo/cestino/meta/foot): quello è di
 * {@link KbCard}, condiviso con i preventivi. Qui si passano solo i dati e gli
 * slot propri del task: spunta di completamento nel `lead` (riusa
 * {@link TaskCompletionToggle}), titolo che apre il drawer, chip tipo + pallino
 * priorità nel `meta`, scadenza (`over`/`today`) + avatar assegnatario nel `foot`.
 *
 * <b>La trascinabilità non è della card, è della bacheca che la ospita</b> — ma tutte e due le
 * bacheche che montano questa card trascinano, e vogliono dire cose diverse:
 *
 * <ul>
 * <li><b>vista per scadenza</b> ({@link TaskList}): le colonne sono derivate dalla data, e l'unico
 * stato persistito è «Completato». Quindi passano solo i due sensi che significano qualcosa —
 * dentro «Completato» lo completa, fuori lo riapre — e gli altri spostamenti si rifiutano con un
 * messaggio. Vedi `handleKanbanMove` in TaskList;</li>
 * <li><b>bacheca di un funnel</b> ({@link taskBoard}): lo spostamento cambia la COLLOCAZIONE del
 * task, che è un asse indipendente dal completamento.</li>
 * </ul>
 *
 * `draggable` non rende trascinabile la card — lo fa la board: dice a {@link KbCard} di fermare il
 * pointer sulle parti cliccabili (spunta, titolo, cestino). Senza, aprire il drawer dalla bacheca
 * diventa un tiro a segno, perché il minimo movimento durante il click avvia il trascinamento
 * invece del click. Si passa sempre: su una board che non trascina non ferma niente e non costa
 * niente.
 *
 * I tre gestori — spunta, apertura, cancellazione — sono <b>opzionali</b>: in bacheca il
 * descrittore passa solo l'elemento, e senza gestori quei comandi <b>non si disegnano</b>. Un
 * cestino che non cancella sarebbe peggio di un cestino assente.
 *
 * Da completato la card prende il modificatore `is-done` (titolo barrato). La spunta resta dov'è:
 * è il gesto per una card sola, il trascinamento quello per spostarne una che si sta già
 * guardando altrove.
 */

import { useTranslation } from 'react-i18next';
import { KbCard } from '../../components/KbCard';
import type { Task } from './taskTypes';
import { TASK_TYPES, TASK_PRIORITIES } from './taskTypes';
import { isTaskCompleted, taskDateBucket } from './taskStatus';
import { initialsFromName } from '@crm/shared';
import { TaskCompletionToggle } from './TaskCompletionToggle';

const dayFmt = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short' });
const timeFmt = new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit' });

export function TaskKanbanCard({
  task,
  assigneeName,
  onToggle,
  onOpen,
  onDelete,
}: {
  task: Task;
  assigneeName?: string;
  onToggle?: () => void;
  onOpen?: () => void;
  onDelete?: () => void;
}) {
  const { t } = useTranslation();
  const done = isTaskCompleted(task);
  const bucket = taskDateBucket(task);

  const typeLabel = TASK_TYPES.find((x) => x.value === task.type)?.label;
  const priorityLabel = TASK_PRIORITIES.find((x) => x.value === task.priority)?.label;

  const due = dueLabel();
  function dueLabel(): { text: string; mod: '' | 'over' | 'today' } {
    if (!task.expirationDate) return { text: t('task.kanban.noDue'), mod: '' };
    const d = new Date(task.expirationDate);
    if (bucket === 'expired') return { text: `${t('task.kanban.overdue')} · ${dayFmt.format(d)}`, mod: 'over' };
    if (bucket === 'expiring_today') return { text: `${t('task.kanban.today')} · ${timeFmt.format(d)}`, mod: 'today' };
    return { text: `${dayFmt.format(d)} · ${timeFmt.format(d)}`, mod: '' };
  }

  return (
    <KbCard
      draggable
      modifier={done ? 'is-done' : undefined}
      lead={onToggle ? <TaskCompletionToggle task={task} onToggle={onToggle} /> : undefined}
      title={task.title}
      onTitleClick={onOpen}
      {...(onDelete
        ? { onDelete, deleteLabel: t('task.kanban.deleteCard') }
        : ({} as { onDelete?: undefined; deleteLabel?: undefined }))}
      meta={
        <>
          <span className="kb-card__tag">{typeLabel ? t(typeLabel) : task.type}</span>
          <span className="kb-card__prio">
            <i className={`task-prio-dot task-prio-dot--${task.priority}`} />
            {priorityLabel ? t(priorityLabel) : task.priority}
          </span>
        </>
      }
      foot={
        <>
          <span className={`kb-card__due${due.mod ? ` is-${due.mod}` : ''}`}>{due.text}</span>
          {assigneeName && <span className="kb-card__avatar">{initialsFromName(assigneeName)}</span>}
        </>
      }
    />
  );
}
